import type { FC } from 'react';
import { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CreativePlayIconButton, PreviewImage } from '../../creatives/components/CreativeComponents';
import { PlayCircleOutlineOutlined } from '@mui/icons-material';
import WellSpyLogo from 'assets/wellspy-logo.svg?react';
import ContentPreviewButtons from './ContentPreviewButtons';

interface IProps {
  original_image_url: string;
  video_sd_url: string;
  video_preview_image_url: string;
  ad_id: string;
  ad_library_url: string;
}

const ContentPreview: FC<IProps> = ({
  original_image_url,
  video_preview_image_url,
  video_sd_url,
  ad_id,
  ad_library_url,
}) => {
  const [isShowVideoPreview, setShowVideoPreview] = useState(false);
  const [isError, setIsError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const isVideo = !!video_sd_url;

  const handlePlay = useCallback(() => {
    setShowVideoPreview(true);
  }, []);

  const handleVideoEnd = useCallback(() => {
    setShowVideoPreview(false);
  }, []);

  if (isError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          gap: 2,
        }}
      >
        <Box sx={{ width: 120, opacity: 0.8 }}>
          <WellSpyLogo />
        </Box>
        <Typography color="text.secondary" variant="body2">
          Content unavailable
        </Typography>
      </Box>
    );
  }

  if (isVideo && isShowVideoPreview) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          bgcolor: 'black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <ContentPreviewButtons
          ad_library_url={ad_library_url}
          video_sd_url={video_sd_url}
          original_image_url={original_image_url}
          video_preview_image_url={video_preview_image_url}
          ad_creative_type="video"
          ad_id={ad_id}
          position="top"
        />
        <Box
          component="video"
          controlsList="nodownload"
          ref={videoRef}
          src={video_sd_url}
          poster={video_preview_image_url}
          autoPlay
          controls
          playsInline
          onEnded={handleVideoEnd}
          onError={() => {
            setIsError(true);
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            outline: 'none',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'black',
        overflow: 'hidden',
      }}
    >
      {isVideo && (
        <CreativePlayIconButton
          onClick={handlePlay}
          sx={{
            position: 'absolute',
            zIndex: 2,
            backgroundColor: 'rgba(0,0,0,0.6)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.8)',
            },
          }}
        >
          <PlayCircleOutlineOutlined sx={{ color: 'white', fontSize: '4rem', transform: 'translateX(-25%)' }} />
        </CreativePlayIconButton>
      )}

      <PreviewImage
        onError={() => {
          setIsError(true);
        }}
        loading="lazy"
        src={isVideo ? video_preview_image_url : original_image_url}
        alt="preview"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
      <ContentPreviewButtons
        ad_library_url={ad_library_url}
        video_sd_url={video_sd_url}
        original_image_url={original_image_url}
        video_preview_image_url={video_preview_image_url}
        ad_creative_type={video_sd_url ? 'video' : 'image'}
        ad_id={ad_id}
      />
    </Box>
  );
};

export default ContentPreview;
